import type { ReactElement } from 'react';
import { portalTimeZone, portalZoneLabel } from '@/shared/geo/tenantLocale';

/**
 * What a notice is, by its `type` (the writers: custom-module automations
 * write `custom_module`; the Employee Hub writes `order`, `event`, `rsvp`).
 * An unknown type still renders, as a plain notice: a new writer never needs
 * a web release to be readable.
 */
export interface NoticeKind {
  /** Small uppercase label above the title. */
  label: string;
  glyph: (size?: number) => ReactElement;
}

const g = (size: number, body: ReactElement) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.6}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    {body}
  </svg>
);

/** A record card with a filled corner (a custom module's record). */
const recordGlyph = (s = 16) =>
  g(s, (
    <>
      <path d="M6 3.5h8.5L19 8v12.5H6z" />
      <path d="M14.5 3.5V8H19" fill="currentColor" fillOpacity={0.32} />
      <path d="M9 12.5h7M9 16h5" />
    </>
  ));

/** A bowl (a food order). */
const orderGlyph = (s = 16) =>
  g(s, (
    <>
      <path d="M3.5 11.5h17a8.5 8.5 0 0 1-17 0Z" fill="currentColor" fillOpacity={0.18} />
      <path d="M3.5 11.5h17a8.5 8.5 0 0 1-17 0Z" />
      <path d="M9 7.5c0-1.4 1-1.6 1-3M13.5 7.5c0-1.4 1-1.6 1-3" />
    </>
  ));

/** A calendar page with the day filled (an event). */
const eventGlyph = (s = 16) =>
  g(s, (
    <>
      <rect x={4} y={5.5} width={16} height={14.5} rx={2.5} />
      <path d="M4 10h16M8.5 3.5v4M15.5 3.5v4" />
      <rect x={13} y={13.5} width={4} height={3.5} rx={0.8} fill="currentColor" fillOpacity={0.32} stroke="none" />
    </>
  ));

/** A person with a tick (someone is coming). */
const rsvpGlyph = (s = 16) =>
  g(s, (
    <>
      <circle cx={9.5} cy={8} r={3.5} fill="currentColor" fillOpacity={0.18} />
      <circle cx={9.5} cy={8} r={3.5} />
      <path d="M3.5 19.5c.6-3.3 3-5 6-5s4.4 1 5.3 2.6" />
      <path d="m15 18 2 2 4-4.5" />
    </>
  ));

/** A loud-hailer (news). */
const newsGlyph = (s = 16) =>
  g(s, (
    <>
      <path d="M4 10v4h3l8 4.5v-13L7 10z" fill="currentColor" fillOpacity={0.18} />
      <path d="M4 10v4h3l8 4.5v-13L7 10z" />
      <path d="M18.5 9.5a3.5 3.5 0 0 1 0 5" />
    </>
  ));

/** The bell itself (anything else). */
export const bellGlyph = (s = 16) =>
  g(s, (
    <>
      <path d="M6 16.5V11a6 6 0 0 1 12 0v5.5l1.5 2h-15z" />
      <path d="M10 20.5a2.2 2.2 0 0 0 4 0" fill="currentColor" fillOpacity={0.32} />
    </>
  ));

const KINDS: Record<string, NoticeKind> = {
  custom_module: { label: 'Module', glyph: recordGlyph },
  order: { label: 'Food order', glyph: orderGlyph },
  event: { label: 'Event', glyph: eventGlyph },
  rsvp: { label: 'RSVP', glyph: rsvpGlyph },
  news: { label: 'News', glyph: newsGlyph },
};

const FALLBACK: NoticeKind = { label: 'Notice', glyph: bellGlyph };

export function noticeKind(type: string | null | undefined): NoticeKind {
  return (type && KINDS[type]) || FALLBACK;
}

/**
 * Where a notice may take you: an in-app path only. A stored link is data,
 * so anything else (another origin, `//host`, `javascript:`) goes nowhere.
 */
export function safeNoticeLink(link: string | null | undefined): string | null {
  const l = (link ?? '').trim();
  if (!l.startsWith('/') || l.startsWith('//') || l.startsWith('/\\')) return null;
  return l;
}

const MIN = 60_000;
const HOUR = 60 * MIN;

function dayKey(d: Date, zone: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
}

/**
 * When a notice arrived, short, on the tenant's portal clock: "Just now",
 * "12 min ago", "3 h ago" (same day), "Yesterday 14:05", "Mon 09:30" (this
 * week), "12 Oct" (older), "12 Oct 2025" (another year).
 */
export function noticeWhen(iso: string | null | undefined, now: Date = new Date(), zone: string = portalTimeZone()): string {
  if (!iso) return '';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  const diff = now.getTime() - at.getTime();
  if (diff < MIN) return 'Just now';
  if (diff < HOUR) return `${Math.floor(diff / MIN)} min ago`;
  const time = new Intl.DateTimeFormat('en-GB', { timeZone: zone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(at);
  const today = dayKey(now, zone);
  const day = dayKey(at, zone);
  if (day === today) return `${Math.floor(diff / HOUR)} h ago`;
  if (day === dayKey(new Date(now.getTime() - 24 * HOUR), zone)) return `Yesterday ${time}`;
  if (diff < 6 * 24 * HOUR) {
    const wd = new Intl.DateTimeFormat('en-GB', { timeZone: zone, weekday: 'short' }).format(at);
    return `${wd} ${time}`;
  }
  const sameYear = day.slice(0, 4) === today.slice(0, 4);
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: zone,
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  }).format(at);
}

/** The full time, for a hover title: "Mon 26 Oct 2026, 14:05 MYT". */
export function noticeFullTime(iso: string | null | undefined, zone: string = portalTimeZone()): string {
  if (!iso) return '';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  const s = new Intl.DateTimeFormat('en-GB', {
    timeZone: zone,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(at);
  return `${s} ${portalZoneLabel()}`;
}

/** The count as the bell prints it. */
export function countLabel(n: number): string {
  return n > 99 ? '99+' : String(n);
}
